<template>
  <header class="app-header">
    <div class="header-container">
      <!-- Logo 和标题 -->
      <div class="logo-section" @click="goHome">
        <img src="/logo.png" alt="VizLab Logo" class="logo" />
        <div class="brand">
          <h1 class="brand-title">VizLab</h1>
          <p class="brand-subtitle">为知实验室</p>
        </div>
      </div>

      <!-- 桌面端导航 -->
      <nav class="desktop-nav">
        <router-link to="/" class="nav-link" exact-active-class="active">
          首页
        </router-link>
        <div class="dropdown" @mouseleave="closeDropdown">
          <button 
            class="nav-link dropdown-toggle"
            :class="{ active: isCategoryActive }"
            @click="toggleDropdown"
            :aria-expanded="categoriesOpen ? 'true' : 'false'"
            aria-haspopup="menu"
          >
            学科分类
            <span class="dropdown-arrow" :class="{ open: categoriesOpen }">▼</span>
          </button>
          <div class="dropdown-menu" v-if="categoriesOpen">
            <router-link 
              v-for="category in categories" 
              :key="category.id"
              :to="`/?category=${category.id}`"
              class="dropdown-item"
              @click="closeDropdown"
            >
              {{ category.icon }} {{ category.name }}
            </router-link>
          </div>
        </div>
        <router-link to="/feedback" class="nav-link">
          💡 反馈
        </router-link>
      </nav>

      <!-- 搜索框（桌面端） -->
      <div class="header-search desktop-only">
        <SearchBar 
          v-model="searchQuery"
          @search="handleSearch"
          :compact="true"
        />
      </div>

      <!-- 移动端菜单按钮 -->
      <button 
        class="mobile-menu-btn mobile-only"
        @click="toggleMobileMenu"
        :aria-label="mobileMenuOpen ? '关闭菜单' : '打开菜单'"
      >
        <span class="menu-icon" :class="{ open: mobileMenuOpen }">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>
    </div>

    <!-- 移动端菜单 -->
    <div class="mobile-menu" :class="{ open: mobileMenuOpen }">
      <!-- 搜索框（移动端） -->
      <div class="mobile-search">
        <SearchBar 
          v-model="searchQuery"
          @search="handleMobileSearch"
        />
      </div>

      <!-- 导航链接 -->
      <nav class="mobile-nav">
        <router-link 
          to="/" 
          class="mobile-nav-link"
          @click="closeMobileMenu"
        >
          🏠 首页
        </router-link>
        <router-link 
          to="/feedback" 
          class="mobile-nav-link"
          @click="closeMobileMenu"
        >
          💡 反馈
        </router-link>
        <router-link 
          v-for="category in categories" 
          :key="category.id"
          :to="`/?category=${category.id}`"
          class="mobile-nav-link"
          @click="closeMobileMenu"
        >
          {{ category.icon }} {{ category.name }}
        </router-link>
      </nav>
    </div>

    <!-- 遮罩层 -->
    <div 
      v-if="mobileMenuOpen" 
      class="mobile-menu-overlay"
      @click="closeMobileMenu"
    ></div>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { SearchBar } from '@/components/shared'
import categoriesData from '@/data/categories.json'

const router = useRouter()
const route = useRoute()

// 分类数据
const categories = categoriesData.categories

// 搜索状态
const searchQuery = ref('')

// 移动端菜单状态
const mobileMenuOpen = ref(false)
const categoriesOpen = ref(false)

// 判断是否在分类页面
const isCategoryActive = computed(() => {
  return !!route.query.category
})

/**
 * 跳转到首页
 */
function goHome() {
  router.push('/')
}

/**
 * 处理搜索
 */
function handleSearch(query) {
  if (query && query.trim()) {
    router.push({ name: 'search', query: { q: query } })
    searchQuery.value = ''
  }
}

/**
 * 处理移动端搜索
 */
function handleMobileSearch(query) {
  handleSearch(query)
  closeMobileMenu()
}

/**
 * 切换移动端菜单
 */
function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
  
  // 防止背景滚动
  if (mobileMenuOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

/**
 * 关闭移动端菜单
 */
function closeMobileMenu() {
  mobileMenuOpen.value = false
  document.body.style.overflow = ''
}

function toggleDropdown() {
  categoriesOpen.value = !categoriesOpen.value
}

function closeDropdown() {
  categoriesOpen.value = false
}
</script>

<style scoped>
.app-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  gap: 2rem;
}

/* Logo 区域 */
.logo-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: opacity 0.2s;
}

.logo-section:hover {
  opacity: 0.8;
}

.logo {
  width: 2.5rem;
  height: 2.5rem;
  object-fit: contain;
  display: block;
}

.brand-title {
  margin: 0;
  font-size: 1.3rem;
  color: #1976d2;
  font-weight: 700;
}

.brand-subtitle {
  margin: 0;
  font-size: 0.75rem;
  color: #666;
}

/* 桌面端导航 */
.desktop-nav {
  display: flex;
  gap: 0.5rem;
  flex: 1;
}

.nav-link {
  padding: 0.5rem 1rem;
  color: #666;
  text-decoration: none;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s;
  white-space: nowrap;
}

.nav-link:hover {
  background-color: #f5f5f5;
  color: #333;
}

.nav-link.active {
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 500;
}

.dropdown {
  position: relative;
}

.dropdown-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  color: #666;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
}

.dropdown-arrow {
  font-size: 0.7rem;
  transition: transform 0.2s ease;
  color: #666;
  margin-left: 0.25rem;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.dropdown-toggle:hover {
  background-color: #f5f5f5;
  color: #333;
}

.dropdown-toggle:hover .dropdown-arrow {
  color: #333;
}

.dropdown-toggle.active {
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 500;
}

.dropdown-toggle.active .dropdown-arrow {
  color: #1976d2;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0,0,0,0.12);
  padding: 0.5rem;
  z-index: 1002;
}

.dropdown-item {
  display: block;
  padding: 0.5rem 0.75rem;
  color: #333;
  text-decoration: none;
  border-radius: 6px;
  white-space: nowrap;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

/* 头部搜索 */
.header-search {
  min-width: 300px;
}

/* 移动端菜单按钮 */
.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  padding: 0.75rem;
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.mobile-menu-btn:hover {
  background-color: #f5f5f5;
}

.mobile-menu-btn:active {
  background-color: #e0e0e0;
}

.menu-icon {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 26px;
}

.menu-icon span {
  display: block;
  height: 3px;
  background-color: #333;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.menu-icon.open span:nth-child(1) {
  transform: rotate(45deg) translate(7px, 7px);
}

.menu-icon.open span:nth-child(2) {
  opacity: 0;
  transform: translateX(-10px);
}

.menu-icon.open span:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -7px);
}

/* 移动端菜单 */
.mobile-menu {
  position: fixed;
  top: 0;
  right: -100%;
  width: 85%;
  max-width: 360px;
  height: 100vh;
  background-color: white;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  z-index: 1001;
  padding: 5rem 0 2rem;
  -webkit-overflow-scrolling: touch;
}

.mobile-menu.open {
  right: 0;
}

.mobile-search {
  padding: 0 1.5rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.mobile-nav {
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
}

.mobile-nav-link {
  padding: 1.125rem 1.5rem;
  color: #333;
  text-decoration: none;
  font-size: 1.0625rem;
  transition: background-color 0.2s;
  border-left: 4px solid transparent;
  min-height: 48px;
  display: flex;
  align-items: center;
  -webkit-tap-highlight-color: transparent;
}

.mobile-nav-link:active {
  background-color: #f0f0f0;
}

.mobile-nav-link.router-link-active {
  background-color: #e3f2fd;
  color: #1976d2;
  border-left-color: #1976d2;
  font-weight: 500;
}

/* 遮罩层 */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  animation: fadeIn 0.3s ease;
  -webkit-tap-highlight-color: transparent;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 响应式 */
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

@media (max-width: 1024px) {
  .desktop-nav {
    display: none;
  }

  .header-search {
    display: none;
  }

  .mobile-menu-btn {
    display: block;
  }

  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: block;
  }
}

@media (max-width: 768px) {
  .header-container {
    padding: 1rem;
    gap: 1rem;
  }

  .brand-title {
    font-size: 1.1rem;
  }

  .brand-subtitle {
    font-size: 0.7rem;
  }

  .logo {
    width: 2rem;
    height: 2rem;
  }

  .mobile-menu {
    width: 90%;
    max-width: 320px;
  }
}

@media (max-width: 480px) {
  .header-container {
    padding: 0.75rem;
  }

  .logo {
    width: 1.75rem;
    height: 1.75rem;
  }

  .brand-title {
    font-size: 1rem;
  }

  .brand-subtitle {
    font-size: 0.65rem;
  }

  .mobile-menu {
    width: 100%;
    max-width: 100%;
  }

  .mobile-nav-link {
    padding: 1rem 1.25rem;
    font-size: 1rem;
  }
}
</style>
